import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Stack, Button, Divider, Chip} from 'views/components';
import {Translate, translate} from 'views/module';
import {
  Send, CheckCircle, Person, Email, Phone,
  DirectionsCar, DirectionsTransit, DirectionsWalk, DirectionsBike,
  Home, Description, Handshake, MoreHoriz,
} from 'views/icons';

import SummaryCard from '../components/SummaryCard';

const ConfirmSection = (props) => {
  const {confirmData = {}, caregiverData = {}, sectionValidity = {}, onNavigateToSection, onSubmitAndProceed} = props;

  // Section index mapping
  const sectionIndices = {
    certifications: 0,
    skillsAndLanguages: 1,
    workHistory: 2,
    personality: 3,
    availability: 4,
    travelPreferences: 5,
    workEligibility: 6,
    contact: 7,
    agreements: 8,
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Handle card click to navigate to section
  const handleCardClick = (sectionKey) => {
    if (onNavigateToSection && sectionIndices[sectionKey] !== undefined) {
      onNavigateToSection(sectionIndices[sectionKey]);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    setIsSubmitting(true);

    // Wait 1 second before proceeding, here we would check if they pass to next round
    await new Promise((resolve) => setTimeout(resolve, 1000));


    // Navigate to interview section after submission
    if (onSubmitAndProceed) {
      await onSubmitAndProceed();
    }
    setIsSubmitting(false);
  };

  // Check if all required sections are completed using sectionValidity prop
  const isApplicationComplete = () => {
    return Object.values(sectionValidity).every((isValid) => isValid === true);
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.Confirm.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 1}}>
        <Translate expression='caregiverScreening.Confirm.Subtitle'/>
      </Typography>
      <Typography variant="body2" color="primary.main" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.Confirm.ClickToEdit'/>
      </Typography>

      <Stack spacing={3}>
        {/* Application Summary Cards */}

        {/* Certifications/Licenses Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.CertificationsTitle')}
          isValid={sectionValidity.certifications}
          invalidMessage={translate('caregiverScreening.Confirm.CertificationsInvalidMessage')}
          onCardClick={() => handleCardClick('certifications')}
        >
          <Stack spacing={1}>
            {
              caregiverData.certifications.map((cert) => (
                <Box key={`selected-cert-${cert.type}`}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {translate(`caregiverScreening.Certifications.AvailableCertifications.${cert.type}`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cert.issuingOrganization}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cert.yearObtainedOrExpected}
                    {' \u2022 '}
                    {translate(`caregiverScreening.Certifications.AvailableCountries.${cert.country}`)}
                    {' \u2022 '}
                    {translate(`caregiverScreening.Certifications.Subdivision${cert.country}.${cert.provinceState}`)}
                    {' \u2022 '}
                    {cert.city}
                  </Typography>
                </Box>
              ))
            }
          </Stack>
        </SummaryCard>

        {/* Skills & Languages Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.SkillsAndLanguagesTitle')}
          isValid={sectionValidity.skillsAndLanguages}
          invalidMessage={translate('caregiverScreening.Confirm.SkillsAndLanguagesInvalidMessage')}
          onCardClick={() => handleCardClick('skillsAndLanguages')}
        >
          {/* Skills SubSummary */}
          <Typography variant="subtitle2" fontWeight={600} sx={{mt: 2, mb: 1}}>
            <Translate expression='caregiverScreening.Confirm.SkillsLabel'/>
          </Typography>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
            {
              caregiverData.skills.map((skill) => (
                <Chip
                  key={skill}
                  label={translate(`caregiverScreening.SkillsAndLanguages.AvailableSkills.${skill}`)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            }
          </Box>
          {/* Languages SubSummary */}
          <Typography variant="subtitle2" fontWeight={600} sx={{mt: 2, mb: 1}}>
            <Translate expression='caregiverScreening.Confirm.LanguagesLabel'/>
          </Typography>
          {
            caregiverData.languages && (
              <>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  {
                    caregiverData.languages.map((lang) => (
                      <Chip
                        key={lang.name}
                        label={(() => {
                          const predefinedLanguage = translate(`caregiverScreening.SkillsAndLanguages.AvailableLanguages.${lang.name}`);
                          const languageLabel = predefinedLanguage !== 'Missing translation' ? predefinedLanguage : lang.name;
                          return `${languageLabel} : ${translate(`caregiverScreening.SkillsAndLanguages.Fluency.${lang.fluency}`)}`;
                        })()}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ))
                  }
                </Box>
              </>
            )
          }
        </SummaryCard>

        {/* Work History Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.WorkHistoryTitle')}
          isValid={sectionValidity.workHistory}
          invalidMessage={translate('caregiverScreening.Confirm.WorkHistoryInvalidMessage')}
          onCardClick={() => handleCardClick('workHistory')}
        >
          <Stack spacing={1}>
            {
              caregiverData.workHistory.slice(0, 3).map((job, index) => (
                <Box key={index}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {job.employerName || translate('caregiverScreening.Confirm.CompanyFallback')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {job.startDate || translate('caregiverScreening.Confirm.StartDateLabel')} - {' '}
                    {job.endDate || translate('caregiverScreening.Confirm.PresentLabel')}
                  </Typography>
                  {
                    job.canContact && (
                      <Typography variant="body2" color="primary.main" sx={{mt: 0.5}}>
                      ✓ {translate('caregiverScreening.Confirm.ReferenceAvailable')}
                      </Typography>
                    )
                  }
                  {
                    job.canContact && job.referenceContactInfo && (
                      <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, fontStyle: 'italic'}}>
                        {translate('caregiverScreening.Confirm.Contact')}: {job.referenceContactInfo}
                      </Typography>
                    )
                  }
                </Box>
              ))
            }
            {
              caregiverData.workHistory.length > 3 && (
                <Typography variant="body2" color="text.secondary">
                  {
                    caregiverData.workHistory.length > 4 ? (
                      <Translate
                        expression='caregiverScreening.Confirm.MorePositions'
                        substitute={{count: caregiverData.workHistory.length - 3}}
                      />
                    ) : (
                     <Translate
                       expression='caregiverScreening.Confirm.OneMorePosition'
                     />
                    )
                  }
                </Typography>
              )
            }
          </Stack>
        </SummaryCard>

        {/* Personality Assessment Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.PersonalityAssessmentTitle')}
          isValid={sectionValidity.personality}
          invalidMessage={translate('caregiverScreening.Confirm.PersonalityInvalidMessage')}
          onCardClick={() => handleCardClick('personality')}
        >
          <Typography variant="body2" color="text.secondary">
            <Translate
              expression='caregiverScreening.Confirm.PersonalityAssessmentCompleted'
              substitute={{count: Object.keys(caregiverData.personality || {}).length}}
            />
          </Typography>
        </SummaryCard>

        {/* Availability Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.WorkAvailabilityTitle')}
          isValid={sectionValidity.availability}
          invalidMessage={translate('caregiverScreening.Confirm.AvailabilityInvalidMessage')}
          onCardClick={() => handleCardClick('availability')}
        >
          {(() => {
            // Time periods definition (matching WorkAvailabilitySection)
            const timePeriods = {
              morning: {
                key: 'morning',
                label: translate('caregiverScreening.WorkAvailability.TimePeriods.Morning.Label'),
                subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Morning.Subtitle'),
                hours: 6,
              },
              afternoon: {
                key: 'afternoon',
                label: translate('caregiverScreening.WorkAvailability.TimePeriods.Afternoon.Label'),
                subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Afternoon.Subtitle'),
                hours: 5,
              },
              evening: {
                key: 'evening',
                label: translate('caregiverScreening.WorkAvailability.TimePeriods.Evening.Label'),
                subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Evening.Subtitle'),
                hours: 4,
              },
              night: {
                key: 'night',
                label: translate('caregiverScreening.WorkAvailability.TimePeriods.Night.Label'),
                subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Night.Subtitle'),
                hours: 9,
              },
            };

            // Calculate total weekly hours
            const totalWeeklyHours = Object.values(caregiverData.availability).reduce((sum, dayShifts) => {
              if (Array.isArray(dayShifts)) {
                return sum + dayShifts.reduce((daySum, periodKey) =>
                  daySum + (timePeriods[periodKey]?.hours || 0), 0,
                );
              }
              return sum;
            }, 0);

            // Define weekday order
            const weekdayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

            // Get available days with details
            const availableDays = weekdayOrder
                .map((dayKey) => {
                  const dayShiftsKey = `${dayKey}Shifts`;
                  const dayShifts = caregiverData.availability[dayShiftsKey];

                  if (!Array.isArray(dayShifts) || dayShifts.length === 0) {
                    return null;
                  }

                  const day = dayKey.replace(/^\w/, (c) => c.toUpperCase());
                  const slots = dayShifts.map((periodKey) => timePeriods[periodKey]).filter(Boolean);
                  const totalHours = slots.reduce((sum, slot) => sum + slot.hours, 0);

                  return {
                    day,
                    slots,
                    totalHours,
                    dayShifts,
                    dayKey,
                  };
                })
                .filter((day) => day !== null);

            if (availableDays.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverScreening.Confirm.NoAvailabilitySelected'/>
                </Typography>
              );
            }

            return (
              <Stack spacing={2}>
                {/* Weekly summary */}
                <Box sx={{
                  p: 2,
                  backgroundColor: '#F5F5F5',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <Typography variant="body1" fontWeight={600}>
                    <Translate expression='caregiverScreening.WorkAvailability.Summary.WeeklyTotal'/>
                  </Typography>
                  <Typography variant="body1" color="primary.main" fontWeight={600}>
                    {totalWeeklyHours} <Translate expression='caregiverScreening.Confirm.Hours'/>
                  </Typography>
                </Box>

                {/* Daily breakdown */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1}}>
                    <Translate expression='caregiverScreening.Confirm.AvailableDays'/>
                  </Typography>
                  <Stack spacing={1}>
                    {
                      availableDays.map(({day, slots, totalHours}) => (
                        <Box
                          key={day}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.5,
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              <Typography component="span" fontWeight={600}>
                                {day}:
                              </Typography>
                              {' '}{slots.map((slot) => slot.label).join(', ')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ml: 2}}>
                            {totalHours} <Translate expression='caregiverScreening.Confirm.HoursShort'/>
                          </Typography>
                        </Box>
                      ))
                    }
                  </Stack>
                </Box>
              </Stack>
            );
          })()}
        </SummaryCard>

        {/* Travel Preferences Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.TravelPreferencesTitle')}
          isValid={sectionValidity.travelPreferences}
          invalidMessage={translate('caregiverScreening.Confirm.TravelInvalidMessage')}
          onCardClick={() => handleCardClick('travelPreferences')}
        >
          {(() => {
            // Transport mode options (matching TravelPreferencesSection)
            const transportModes = [
              {
                id: 'car',
                label: translate('caregiverScreening.TravelPreferences.TransportModes.Car.Label'),
                icon: DirectionsCar,
                description: translate('caregiverScreening.TravelPreferences.TransportModes.Car.Description'),
              },
              {
                id: 'transit',
                label: translate('caregiverScreening.TravelPreferences.TransportModes.Transit.Label'),
                icon: DirectionsTransit,
                description: translate('caregiverScreening.TravelPreferences.TransportModes.Transit.Description'),
              },
              {
                id: 'walking',
                label: translate('caregiverScreening.TravelPreferences.TransportModes.Walking.Label'),
                icon: DirectionsWalk,
                description: translate('caregiverScreening.TravelPreferences.TransportModes.Walking.Description'),
              },
              {
                id: 'cycling',
                label: translate('caregiverScreening.TravelPreferences.TransportModes.Cycling.Label'),
                icon: DirectionsBike,
                description: translate('caregiverScreening.TravelPreferences.TransportModes.Cycling.Description'),
              },
            ];

            const selectedMode = caregiverData.travelPreferences.transportMode;
            const maxTravelTime = caregiverData.travelPreferences.maxTravelTime || 30;
            const selectedTransport = transportModes.find((mode) => mode.id === selectedMode);
            if (!selectedTransport) {
              return null;
            } else {
              const IconComponent = selectedTransport.icon;

              // Format time for display
              const formatTravelTime = (minutes) => {
                if (minutes === 60) {
                  return '1 hour';
                }
                return `${minutes} minutes`;
              };

              return (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      backgroundColor: '#F5F5F5',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <IconComponent sx={{fontSize: 24, color: 'primary.main'}} />
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedTransport.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedTransport.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{textAlign: 'right'}}>
                      <Typography variant="body1" color="primary.main" fontWeight={600}>
                        {formatTravelTime(maxTravelTime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <Translate expression='caregiverScreening.Confirm.MaxTravelTime'/>
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              );
            }
          })()}
        </SummaryCard>

        {/* Work Eligibility Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.WorkEligibilityTitle')}
          isValid={sectionValidity.workEligibility}
          invalidMessage={translate('caregiverScreening.Confirm.EligibilityInvalidMessage')}
          onCardClick={() => handleCardClick('workEligibility')}
        >
          {(() => {
            // Work eligibility options (matching WorkEligibilitySection)
            const workEligibilityOptions = [
              {
                id: 'citizenPermanent',
                label: translate('caregiverScreening.WorkEligibility.Options.CitizenPermanent.Label'),
                icon: Home,
                description: translate('caregiverScreening.WorkEligibility.Options.CitizenPermanent.Description'),
              },
              {
                id: 'temporaryResident',
                label: translate('caregiverScreening.WorkEligibility.Options.TemporaryResident.Label'),
                icon: Description,
                description: translate('caregiverScreening.WorkEligibility.Options.TemporaryResident.Description'),
              },
              {
                id: 'seekingSponsorship',
                label: translate('caregiverScreening.WorkEligibility.Options.SeekingSponsorship.Label'),
                icon: Handshake,
                description: translate('caregiverScreening.WorkEligibility.Options.SeekingSponsorship.Description'),
              },
              {
                id: 'other',
                label: translate('caregiverScreening.WorkEligibility.Options.Other.Label'),
                icon: MoreHoriz,
                description: translate('caregiverScreening.WorkEligibility.Options.Other.Description'),
              },
            ];

            const selectedEligibility = caregiverData.workEligibility.eligibilityStatus;
            if (!selectedEligibility) {
              return null;
            } else {
              const selectedOption = workEligibilityOptions.find((option) => option.id === selectedEligibility);
              const IconComponent = selectedOption.icon;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    backgroundColor: '#F5F5F5',
                    borderRadius: 1,
                  }}
                >
                  <IconComponent sx={{fontSize: 24, color: 'primary.main'}} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedOption.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOption.description}
                    </Typography>
                  </Box>
                </Box>
              );
            }
          })()}
        </SummaryCard>

        {/* Contact Information Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.ContactInformationTitle')}
          isValid={sectionValidity.contact}
          invalidMessage={translate('caregiverScreening.Confirm.ContactInvalidMessage')}
          onCardClick={() => handleCardClick('contact')}
        >
          <Stack spacing={1}>
            {
              caregiverData.contact.firstName && caregiverData.contact.lastName && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person fontSize="small" />
                  <Typography variant="body2">
                    {caregiverData.contact.firstName} {caregiverData.contact.lastName}
                  </Typography>
                </Stack>
              )
            }
            {
              caregiverData.contact.email && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Email fontSize="small" />
                  <Typography variant="body2">
                    {caregiverData.contact.email}
                  </Typography>
                </Stack>
              )
            }
            {
              caregiverData.contact.phone && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">
                    {caregiverData.contact.phone}
                  </Typography>
                </Stack>
              )
            }
            {
              (caregiverData.contact.streetAddress && caregiverData.contact.city && caregiverData.contact.province && caregiverData.contact.postalCode) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Home fontSize="small" />
                  <Typography variant="body2">
                    {
                      [
                        caregiverData.contact.streetAddress,
                        caregiverData.contact.city,
                        caregiverData.contact.province,
                        caregiverData.contact.postalCode,
                      ].join(', ')
                    }
                  </Typography>
                </Stack>
              )
            }
          </Stack>
        </SummaryCard>

        {/* Agreements Summary */}
        <SummaryCard
          title={translate('caregiverScreening.Confirm.AgreementsTitle')}
          isValid={sectionValidity.agreements}
          invalidMessage={translate('caregiverScreening.Confirm.AgreementsInvalidMessage')}
          onCardClick={() => handleCardClick('agreements')}
        >
          <Stack spacing={1}>
            {
              sectionValidity.agreements && (
                Object.entries(caregiverData.agreements).map(([key]) => (
                  <Box key={key}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {translate(`caregiverScreening.Agreements.Items.${key}`).Label}
                    </Typography>
                  </Box>
                ))
              )
            }
          </Stack>
        </SummaryCard>

        <Divider />

        {/* Submit Section */}
        <Box
          sx={{
            p: 3,
            backgroundColor: isApplicationComplete() ? '#E8F5E8' : '#FFF3E0',
            borderRadius: 2,
            border: `1px solid ${isApplicationComplete() ? '#C8E6C9' : '#FFE0B2'}`,
            textAlign: 'center',
          }}
        >
          {
            isApplicationComplete() ? (
            <>
              <CheckCircle color="success" sx={{fontSize: 48, mb: 2}} />
              <Typography variant="h6" fontWeight={600} sx={{mb: 1}}>
                <Translate expression='caregiverScreening.Confirm.ApplicationReadyTitle'/>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                <Translate expression='caregiverScreening.Confirm.ApplicationReadyDescription'/>
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={<Send />}
                onClick={handleSubmit}
                disabled={confirmData.submitted}
                loading={isSubmitting}
                sx={{
                  'px': 4,
                  'py': 1.5,
                  'color': 'white',
                  '&:disabled': {
                    color: 'white',
                  },
                }}
              >
                { confirmData.submitted && !isSubmitting ?
                  translate('caregiverScreening.Confirm.ApplicationSubmitted') :
                  translate('caregiverScreening.Confirm.SubmitButton')
                }
              </Button>
              {
                confirmData.submitted && !isSubmitting && (
                  <Typography variant="body2" color="success.main" sx={{mt: 2}}>
                    <Translate expression='caregiverScreening.Confirm.SubmittedSuccessfully'/>
                  </Typography>
                )
              }
            </>
            ) : (
            <>
              <Typography variant="h6" fontWeight={600} sx={{mb: 1}}>
                <Translate expression='caregiverScreening.Confirm.CompleteAllSectionsTitle'/>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                <Translate expression='caregiverScreening.Confirm.CompleteAllSectionsDescription'/>
              </Typography>
              <Button
                variant="outlined"
                disabled
                sx={{px: 4, py: 1.5}}
              >
                <Translate expression='caregiverScreening.Confirm.CompleteAllSectionsButton'/>
              </Button>
            </>
            )
          }
        </Box>
      </Stack>
    </Box>
  );
};

ConfirmSection.propTypes = {
  onConfirmChange: PropTypes.func,
  confirmData: PropTypes.object,
  sectionValidity: PropTypes.object,
  onNavigateToSection: PropTypes.func,
  onSubmitAndProceed: PropTypes.func,
  caregiverData: PropTypes.shape({
    skills: PropTypes.array,
    workHistory: PropTypes.array,
    personality: PropTypes.object,
    availability: PropTypes.object,
    travelPreferences: PropTypes.object,
    workEligibility: PropTypes.object,
    contact: PropTypes.object,
  }),
};

export default ConfirmSection;
